import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  interval,
  map,
  Observable,
  startWith,
  switchMap
} from 'rxjs';

import { BonusWheelService } from '@app/modules/bonus-wheel/bonus-wheel.service';
import { AuthState } from '@app/modules/users';

@Component({
  selector: 'bonus-wheel-banner',
  templateUrl: './bonus-wheel.component.html',
  styleUrl: './bonus-wheel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink]
})
export default class BonusWheelBannerComponent implements OnInit, AfterViewInit {
  private readonly bonusWheelService: BonusWheelService = inject(BonusWheelService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  public readonly authState = inject(AuthState);

  public spinsCount = signal<number>(0);
  public isBonusWheelLoading = signal<boolean>(false);

  public timerData = signal({
    days: ['00', 0],
    hours: ['00', 0],
    minutes: ['00', 0],
    seconds: ['00', 0],
    isTimerZero: false
  });

  private timerDataCalculated(endTime: string): Observable<void> {
    return interval(1000).pipe(
      startWith(0),
      map(() => {
        const currentTime = new Date().getTime();
        const endTimeMs = new Date(endTime).getTime();
        const timeLeft = endTimeMs - currentTime;

        if (timeLeft <= 0) {
          this.timerData.set({
            days: ['00', 0],
            hours: ['00', 0],
            minutes: ['00', 0],
            seconds: ['00', 0],
            isTimerZero: true
          });

          return;
        }

        // Расчет дней, часов, минут и секунд
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

        // Добавляем ведущие нули
        const formatNumber = (num: number): [string, number] => {
          return [num < 10 ? `0${num}` : `${num}`, num];
        };

        this.timerData.set({
          days: formatNumber(days),
          hours: formatNumber(hours),
          minutes: formatNumber(minutes),
          seconds: formatNumber(seconds),
          isTimerZero: false
        });
      })
    );
  }

  ngOnInit(): void {
    this.bonusWheelService
      .getBonusWheelStates()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((response) => this.timerDataCalculated(response.cooldownEndsAt).pipe(map(() => response)))
      )
      .subscribe((response) => {
        this.spinsCount.set(response.extraSpins);

        if (this.timerData().isTimerZero) {
          this.spinsCount.update((value) => value + 1);
        }
      });

    // Инициализируем CSS переменную с начальной скоростью
    document.documentElement.style.setProperty('--wheel-duration', '10s');
    document.documentElement.style.setProperty('--wheel-animation-name', 'rotate');
  }

  @ViewChild('wheel') public wheelElementRef!: ElementRef<HTMLElement>;
  @ViewChild('spinArrows') public spinArrowsElement!: ElementRef<HTMLElement>;
  private arrowsAnimationFrame: number | null = null;
  private arrowsCurrentDegree = 0;
  private readonly arrowsRotationSpeed = 120; // градусов в секунду (скорость постоянного вращения)
  private readonly arrowsSlowdownSpeed = 60; // градусов в секунду (скорость замедления при уходе hover)
  private lastTimestamp: number | null = null;

  private currentRotation = 0;
  private targetDegreePerSecond = 18;
  private currentDegreePerSecond = 18;
  private _lastUpdateTime?: number;

  ngAfterViewInit(): void {
    this.startTrackingRotation();
  }

  private startTrackingRotation(): void {
    requestAnimationFrame(() => this.updateCurrentRotation());
  }

  private updateCurrentRotation(): void {
    const now = performance.now();
    const elapsed = now - (this._lastUpdateTime || now);

    this._lastUpdateTime = now;

    // Плавно изменяем текущую скорость к целевой
    if (this.currentDegreePerSecond !== this.targetDegreePerSecond) {
      const step = elapsed * 0.05; // Регулируем скорость изменения скорости

      if (this.currentDegreePerSecond < this.targetDegreePerSecond) {
        this.currentDegreePerSecond = Math.min(this.targetDegreePerSecond, this.currentDegreePerSecond + step);
      } else {
        this.currentDegreePerSecond = Math.max(this.targetDegreePerSecond, this.currentDegreePerSecond - step);
      }
    }

    // Рассчитываем инкремент поворота на основе текущей скорости
    this.currentRotation = (this.currentRotation + (this.currentDegreePerSecond * elapsed) / 1000) % 360;

    // Применяем текущую позицию вращения, а не используем CSS анимацию
    if (this.wheelElementRef?.nativeElement) {
      this.wheelElementRef.nativeElement.style.transform = `rotate(${this.currentRotation}deg)`;
    }

    requestAnimationFrame(() => this.updateCurrentRotation());
  }

  onMouseEnter(): void {
    // Остановить предыдущую анимацию замедления, если она запущена
    if (this.arrowsAnimationFrame !== null) {
      cancelAnimationFrame(this.arrowsAnimationFrame);
      this.arrowsAnimationFrame = null;
    }

    // Сохранить текущий угол стрелок
    if (this.spinArrowsElement?.nativeElement) {
      const currentTransform = getComputedStyle(this.spinArrowsElement.nativeElement).transform;

      if (currentTransform !== 'none') {
        const matrix = new DOMMatrix(currentTransform);

        this.arrowsCurrentDegree = Math.round(Math.atan2(matrix.b, matrix.a) * (180 / Math.PI));
      }
    }

    // Запустить анимацию вращения с постоянной скоростью
    this.lastTimestamp = performance.now();
    this.animateArrows();

    // Увеличиваем скорость в несколько раз для более заметного эффекта
    this.targetDegreePerSecond = 108; // 360 градусов / 3.33 сек = 108 градусов/сек
  }

  onMouseLeave(): void {
    // Остановить анимацию вращения
    if (this.arrowsAnimationFrame !== null) {
      cancelAnimationFrame(this.arrowsAnimationFrame);
      this.arrowsAnimationFrame = null;
    }

    // Запустить анимацию замедления
    this.lastTimestamp = performance.now();
    this.animateArrowsSlowdown();

    // Возвращаем к обычной скорости
    this.targetDegreePerSecond = 18;
  }

  private animateArrows(): void {
    const now = performance.now();
    const elapsed = (now - (this.lastTimestamp || now)) / 1000; // в секундах

    this.lastTimestamp = now;

    // Увеличить текущий угол на основе скорости вращения и прошедшего времени
    this.arrowsCurrentDegree = (this.arrowsCurrentDegree + this.arrowsRotationSpeed * elapsed) % 360;

    // Применить трансформацию
    if (this.spinArrowsElement?.nativeElement) {
      this.spinArrowsElement.nativeElement.style.transform = `rotate(${this.arrowsCurrentDegree}deg)`;
    }

    // Запланировать следующий кадр анимации
    this.arrowsAnimationFrame = requestAnimationFrame(() => this.animateArrows());
  }

  // eslint-disable-next-line max-statements
  private animateArrowsSlowdown(): void {
    const now = performance.now();
    const elapsed = (now - (this.lastTimestamp || now)) / 1000; // в секундах

    this.lastTimestamp = now;

    // Определить целевой угол (ближайшие 360n градусов вперёд)
    const currentAngle = this.arrowsCurrentDegree % 360;

    // Всегда двигаемся к следующему полному кругу (0, 360, 720...)
    // Это гарантирует, что мы всегда крутимся вперёд до полного круга
    const targetDegree = Math.ceil(currentAngle / 360) * 360;

    // Если мы точно на отметке 360n, движемся к следующей отметке 360n
    if (currentAngle === 0) {
      const nextTarget = this.arrowsCurrentDegree + 360;

      this.arrowsCurrentDegree = nextTarget;

      // Применяем трансформацию сразу для избежания дергания
      if (this.spinArrowsElement?.nativeElement) {
        this.spinArrowsElement.nativeElement.style.transform = `rotate(${this.arrowsCurrentDegree}deg)`;
      }

      this.arrowsAnimationFrame = null;

      return;
    }

    // Шаг замедления
    const step = this.arrowsSlowdownSpeed * elapsed;

    // Рассчитываем расстояние до цели (всегда положительное, т.к. всегда движемся вперёд)
    let distance = targetDegree - this.arrowsCurrentDegree;

    // Обрабатываем особый случай перехода через 360
    if (distance < 0) {
      distance += 360;
    }

    // Если мы достаточно близко к цели или шаг больше расстояния, устанавливаем точное целевое значение
    if (distance <= step || distance < 0.5) {
      this.arrowsCurrentDegree = targetDegree;
    } else {
      // Иначе делаем шаг вперёд к цели
      this.arrowsCurrentDegree = (this.arrowsCurrentDegree + step) % 720; // Используем модуль 720 для корректной работы с большими углами
    }

    // Применяем трансформацию
    if (this.spinArrowsElement?.nativeElement) {
      this.spinArrowsElement.nativeElement.style.transform = `rotate(${this.arrowsCurrentDegree}deg)`;
    }

    // Продолжаем анимацию, пока не достигнем цели (с погрешностью)
    if (Math.abs(targetDegree - this.arrowsCurrentDegree) > 0.5) {
      this.arrowsAnimationFrame = requestAnimationFrame(() => this.animateArrowsSlowdown());
    } else {
      // Устанавливаем точное значение, когда достигли цели
      if (this.spinArrowsElement?.nativeElement) {
        this.spinArrowsElement.nativeElement.style.transform = `rotate(${targetDegree}deg)`;
      }

      this.arrowsCurrentDegree = targetDegree;
      this.arrowsAnimationFrame = null;
    }
  }
}
